'use client'

import { graphql } from '~/gql'
import { useGQLQuery } from '~/hooks/useGraphQL'
import { useBaseEvaluation } from './EvaluationDocumentProvider'
import { useDashboardWorkspaceContext } from '~/app/(dashboard)/dashboardContext'
import { useCallback, useMemo } from 'react'
import {
  CollaboratorStatus,
  EvaluationVersionClientQuery,
  MembersListQuery,
} from '~/gql/graphql'
import { StandardRoles } from './evaluation-permissions'

export const collaboratorsQuery = graphql(`
  query GetCollaborators($evaluationId: ID!) {
    collaborators(evaluationId: $evaluationId) {
      id
      roles
      name
      avatar
      email
      status
      allowPermissions
    }
  }
`)

export const useCollaboratorsQuery = ({
  evaluationId,
  enabled,
}: {
  evaluationId: string
  enabled?: boolean
}) =>
  useGQLQuery(
    collaboratorsQuery,
    { evaluationId },
    {
      enabled:
        !!evaluationId && (typeof enabled === 'undefined' || enabled === true),
    }
  )

function getCollaboratorRolesFromWorkspaceMember(
  member: MembersListQuery['getWorkspaceMembers'][0]
): StandardRoles[] {
  if (!member.roles) {
    return [StandardRoles.viewer]
  }
  const roles = member.roles
    .toLowerCase()
    .split(',')
    .map((role) => role.trim())
    .filter((role) => role.length > 0)
  
  if (roles.includes('admin') || roles.includes('owner')) {
    return [StandardRoles.admin]
  } else if (roles.includes('member') || roles.includes('editor')) {
    return [StandardRoles.editor]
  }
  return [StandardRoles.viewer]
}

export type EvaluationCollaborator = {
  avatar: string
  email: string
  id: string
  name: string
  roles: string[]
  status: CollaboratorStatus
  type: 'evaluation' | 'workspace-member'
  allowPermissions: string[]
}

export function useGetCombinedCollaborators() {
  const {
    memberId,
    workspace: { id: workspaceId },
    workspaceMembers: {
      members,
      refetchWorkspaceMembers,
      workspaceMembersQueryStatus,
    },
  } = useDashboardWorkspaceContext()

  const getCombinedCollaborators = useCallback(
    (
      evaluationCollaborators?: EvaluationVersionClientQuery['evaluation']['collaborators']
    ) => {
      const collaborators = new Map<string, EvaluationCollaborator>([])
      evaluationCollaborators?.forEach((collaborator) => {
        if (!collaborator.id) {
          return
        }
        if (collaborators.has(collaborator.id)) {
          return
        }
        collaborators.set(collaborator.id, {
          avatar: collaborator.avatar ?? '',
          email: collaborator.email ?? '',
          id: collaborator.id ?? '',
          name: collaborator.name ?? '',
          roles: collaborator.roles ?? [],
          status: collaborator.status ?? CollaboratorStatus.NotInvited,
          type: 'evaluation',
          allowPermissions: collaborator.allowPermissions ?? [],
        })
      })
      members.forEach((member) => {
        if (collaborators.has(member.memberId)) {
          return
        }
        collaborators.set(member.memberId, {
          avatar: member.user?.profile?.avatar ?? '',
          email: member.user?.profile?.email ?? '',
          id: member.memberId,
          name: member.user?.profile?.fullName ?? '',
          roles: getCollaboratorRolesFromWorkspaceMember(member),
          status:
            (member.inviteStatus as CollaboratorStatus) ??
            CollaboratorStatus.NotInvited,
          type: 'workspace-member',
          allowPermissions: member.permissions?.allow ?? [],
        })
      })
      const collaboratorArray = Array.from(collaborators.values())
      const collaborator = collaboratorArray.find(
        (collaborator) => collaborator.id === memberId
      )
      return {
        collaborator,
        collaborators: collaboratorArray,
      }
    },
    [members, memberId]
  )

  return {
    getCombinedCollaborators,
    workspaceMembersQueryStatus,
    refetchWorkspaceMembers,
    workspaceId,
  }
}

export function useBaseEvaluationCollaborators() {
  const { evaluation } = useBaseEvaluation()
  const {
    data: collaboratorsQueryData,
    status,
    refetch,
  } = useCollaboratorsQuery({
    evaluationId: evaluation?.id ?? '',
  })

  const {
    getCombinedCollaborators,
    workspaceMembersQueryStatus,
    refetchWorkspaceMembers,
    workspaceId,
  } = useGetCombinedCollaborators()

  const combinedCollaborators = useMemo(() => {
    return {
      ...getCombinedCollaborators(collaboratorsQueryData?.collaborators),
      collaboratorsQueryStatus: status,
      refetchCollaborators: refetch,
      workspaceMembersQueryStatus,
      refetchWorkspaceMembers,
      workspaceId,
      evaluationId: evaluation?.id,
    }
  }, [
    workspaceId,
    evaluation?.id,
    getCombinedCollaborators,
    collaboratorsQueryData,
  ])

  return combinedCollaborators
}
